import _ from "lodash";
import { normalizeSkuPhotoMappingTargets } from "../drive/skuPhotoMappingUtils";

export const ICH_ORG_ID = "a25f9e42-23cd-11ec-9068-066917b38554";
export const ICH_STRUCTURAL_ACTIVITY_ID = "caeea8b7-1e4d-5cc6-98ee-22e1d7597ae5";
export const SKU_PHOTO_MAPPING_PROTOCOL_VERSION = 1;

const ICH_STRUCTURAL_ACTIVITY_NAME = "ich - structural selection";
const ICH_STRUCTURAL_ACTIVITY_TAGS = ["ich", "structural", "sku-filters"];
const SKU_PHOTO_MAPPING_ACTIVITY_TAG = "sku-photo-mapping-workbench";

function normalizeActivityTags(activity = {}) {
	return (_.isArray(activity.tags) ? activity.tags : [])
		.map((tag) => _.toString(_.isPlainObject(tag) ? (tag.id || tag.name || tag.value) : tag).trim().toLowerCase())
		.filter(Boolean);
}

export function isSkuPhotoMappingActivity(activity = {}) {
	if (!_.isPlainObject(activity)) {
		return false;
	}

	const activityId = _.toString(activity.id || "").trim();
	if (activityId === ICH_STRUCTURAL_ACTIVITY_ID) {
		return true;
	}

	if (_.get(activity, ["authorProperties", "skuPhotoMappingWorkbench"]) === true) {
		return true;
	}

	const activityName = _.toString(activity.name || activity.title || "").trim().toLowerCase();
	const tags = normalizeActivityTags(activity);
	return tags.includes(SKU_PHOTO_MAPPING_ACTIVITY_TAG)
		|| (activityName.startsWith(ICH_STRUCTURAL_ACTIVITY_NAME)
			&& ICH_STRUCTURAL_ACTIVITY_TAGS.every((tag) => tags.includes(tag)));
}

export function getSkuPhotoMappingActivityCandidates(activities = []) {
	const candidatesById = new Map();
	(_.isArray(activities) ? activities : []).forEach((activity) => {
		const activityId = _.toString(_.get(activity, "id") || "").trim();
		if (!activityId || _.get(activity, "newDoc") === true || !isSkuPhotoMappingActivity(activity)) {
			return;
		}
		candidatesById.set(activityId, activity);
	});

	return Array.from(candidatesById.values()).sort((left, right) => {
		const leftIsCanonical = _.toString(left.id || "").trim() === ICH_STRUCTURAL_ACTIVITY_ID;
		const rightIsCanonical = _.toString(right.id || "").trim() === ICH_STRUCTURAL_ACTIVITY_ID;
		if (leftIsCanonical !== rightIsCanonical) {
			return leftIsCanonical ? -1 : 1;
		}

		const leftTimestamp = Number(left.revisionTs || left.updatedAt || left.createdAt || 0) || 0;
		const rightTimestamp = Number(right.revisionTs || right.updatedAt || right.createdAt || 0) || 0;
		if (leftTimestamp !== rightTimestamp) {
			return rightTimestamp - leftTimestamp;
		}

		return _.toString(left.name || left.title || left.id).localeCompare(
			_.toString(right.name || right.title || right.id)
		);
	});
}

export function resolveSkuPhotoMappingActivity(activities = [], preferredActivityId = "") {
	const candidates = getSkuPhotoMappingActivityCandidates(activities);
	const preferredId = _.toString(preferredActivityId || "").trim();
	return candidates.find((activity) => _.toString(activity.id || "").trim() === preferredId)
		|| candidates[0]
		|| null;
}

export function isSkuPhotoMappingRequested(search = "") {
	try {
		const params = new URLSearchParams(_.toString(search || ""));
		const value = _.toString(params.get("skuPhotoMapping") || "").trim().toLowerCase();
		return value === "1" || value === "true" || value === "yes";
	} catch (err) {
		return false;
	}
}

export function isSkuPhotoMappingEnabled({ search, orgId, activity, readOnly = false } = {}) {
	const authorProperties = _.get(activity, "authorProperties");
	const pricingSplitScreenEligible = !_.isPlainObject(authorProperties)
		|| authorProperties.pricingSplitScreen === true;
	return isSkuPhotoMappingRequested(search)
		&& _.toString(orgId || "").trim() === ICH_ORG_ID
		&& isSkuPhotoMappingActivity(activity)
		&& pricingSplitScreenEligible
		&& readOnly !== true;
}

export function normalizeSkuPhotoMappingSelectionPayload(payload, trustedContext = {}) {
	if (!_.isPlainObject(payload)
		|| payload.protocolVersion !== SKU_PHOTO_MAPPING_PROTOCOL_VERSION
		|| !Array.isArray(payload.selections)) {
		return null;
	}

	const contextId = _.toString(payload.contextId || "").trim();
	if (!contextId) {
		return null;
	}

	const validSelections = payload.selections.every((selection) => (
		_.isPlainObject(selection)
		&& _.toString(selection.objectID || "").trim() !== ""
		&& _.toString(selection.sku || "").trim() !== ""
	));
	if (!validSelections) {
		return null;
	}

	return {
		...payload,
		method: "skuPhotoMappingSelectionChanged",
		protocolVersion: SKU_PHOTO_MAPPING_PROTOCOL_VERSION,
		contextId,
		indexName: _.toString(payload.indexName || "").trim(),
		pricingSnapshotId: _.toString(payload.pricingSnapshotId || "").trim(),
		selections: payload.selections,
		orgId: _.toString(trustedContext.orgId || "").trim(),
		activityId: _.toString(trustedContext.activityId || "").trim()
	};
}

export function getSkuPhotoMappingHeaderModel(selections = [], selectedPhotoCount = 0, options = {}) {
	const targets = normalizeSkuPhotoMappingTargets(selections, options);
	const photoCount = Math.max(0, Number(selectedPhotoCount) || 0);
	const targetLabels = targets.map((target) => {
		const sku = _.toString(target.sku || "").trim();
		const description = _.toString(
			_.get(target, ["display", "desc"])
			|| _.get(target, ["display", "subtitle"])
			|| ""
		).trim();
		return description ? `${sku} — ${description}` : sku;
	}).filter(Boolean);
	const targetPreviewLimit = 2;
	const targetOverflowCount = Math.max(0, targetLabels.length - targetPreviewLimit);
	const targetPreview = targetLabels.slice(0, targetPreviewLimit).join("; ");
	const skuCount = targets.length;
	const firstSearchText = _.toString(
		_.get(targets, [0, "display", "desc"])
		|| _.get(targets, [0, "display", "subtitle"])
		|| _.get(targets, [0, "sku"])
		|| ""
	).trim();

	let nextStep;
	if (skuCount === 0) {
		nextStep = "Step 1: choose an outline item, then click Use for Mapping on one or more SKU cards.";
	} else if (photoCount === 0) {
		nextStep = firstSearchText
			? `Step 2: search or browse Drive for “${firstSearchText},” then check one or more matching photos.`
			: "Step 2: search or browse Drive, then check one or more matching photos.";
	} else {
		nextStep = `Ready: click “Link ${photoCount} photo${photoCount === 1 ? "" : "s"} to ${skuCount} SKU${skuCount === 1 ? "" : "s"}” on the right.`;
	}

	return {
		skuCount,
		photoCount,
		targetLabels,
		targetPreview,
		targetOverflowCount,
		nextStep
	};
}

export function getSkuPhotoMappingLaunchPath(activityId = ICH_STRUCTURAL_ACTIVITY_ID) {
	return `/activity/${encodeURIComponent(activityId)}?skuPhotoMapping=1`;
}
